import React from 'react';
import { Controller } from 'react-hook-form';
import { useBeamDetailing } from '../../hooks/useBeamDetailing';

const BlockReinforcement = ({
  register,
  control,
  errors,
  renderError,
  selectedLength,
  setValue,
  lengthOptions,
  hookOptions,
  diameterOptions,
  topBarFields,
  appendTopBarGroup,
  removeTopBarGroup,
  bottomBarFields,
  appendBottomBarGroup,
  removeBottomBarGroup,
  totalTopBars,
  totalBottomBars,
  topBarsGroupError,
  bottomBarsGroupError,
  segmentReinforcementFields = [],
  appendSegmentReinforcement,
  removeSegmentReinforcement,
  segmentReinforcementsError,
  spanOptions = [],
  stirrupFields,
  appendStirrup,
  removeStirrup,
  lapSpliceLength,
  isLapSpliceLoading,
  concreteStrength,
  onComputeDetailing,
  isDetailingComputing = false,
  detailingError = null,
  nsrWarnings = [],
  stirrupTypeOptions = [],
}) => {
  const {
    isComputing: fallbackDetailingComputing,
    error: fallbackDetailingError,
    computeDetailing: fallbackComputeDetailing,
  } = useBeamDetailing();

  const hasExternalDetailing = typeof onComputeDetailing === 'function';
  const detailingHandler = hasExternalDetailing ? onComputeDetailing : fallbackComputeDetailing;
  const detailingIsComputing = hasExternalDetailing ? isDetailingComputing : fallbackDetailingComputing;
  const detailingStatusError = hasExternalDetailing ? detailingError : fallbackDetailingError;
  const hookWarnings = Array.isArray(nsrWarnings) ? nsrWarnings : [];

  const handleDetailingClick = () => {
    if (!detailingHandler) {
      return;
    }
    detailingHandler();
  };

  return (
    <div className="space-y-6 bg-[#030a18]/70 border border-slate-900 rounded-3xl p-6 shadow-[0_18px_70px_rgba(2,6,23,0.6)]">
    <header className="flex items-center justify-between">
      <div>
        <p className="text-xs uppercase tracking-[0.4em] text-slate-500">Bloque 03</p>
        <h2 className="text-lg font-semibold">Armaduras longitudinales y transversales</h2>
      </div>
      <span className="text-xs text-slate-400">Barras · Φ · L<sub>máx</sub> · traslapos</span>
    </header>

    <div className="bg-[#0b172f] border border-slate-800 rounded-3xl p-5 space-y-4">
      <p className="text-[11px] uppercase tracking-[0.4em] text-slate-500">Parámetros generales</p>
      <div className="grid md:grid-cols-3 gap-5">
        <div>
          <label className="label">L. máxima barra</label>
          <div className="flex gap-2">
            {lengthOptions.map((length) => (
              <button
                type="button"
                key={length}
                onClick={() => setValue('max_rebar_length_m', length, { shouldDirty: true })}
                className={`flex-1 py-2 rounded-lg border text-sm font-semibold transition-colors ${
                  selectedLength === length ? 'bg-primary/90 text-white border-primary' : 'bg-transparent border-slate-700 text-slate-300'
                }`}
              >
                {length}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="label">Gancho</label>
          <select className="input" {...register('hook_type')}>
            {hookOptions.map((hook) => (
              <option key={hook.value} value={hook.value}>
                {hook.label}
              </option>
            ))}
          </select>
          {renderError('hook_type')}
          {hookWarnings.map((warning) => (
            <p key={warning} className="text-amber-300 text-xs mt-1">
              {warning}
            </p>
          ))}
        </div>
        <div>
          <label className="label">Recubrimiento (cm)</label>
          <input type="number" min={1} step={1} className="input" {...register('cover_cm', { valueAsNumber: true })} />
          {renderError('cover_cm')}
        </div>
      </div>
    </div>

    <div className="rounded-3xl border border-slate-800 bg-[#040a16]/50 p-5 space-y-5">
      <p className="text-[11px] uppercase tracking-[0.4em] text-slate-500">Barras Constantes a lo largo de la Viga</p>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-slate-800 bg-[#050b16]/40 p-4 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="label">Barras superiores</p>
              <p className="text-xs text-slate-400">Total: {totalTopBars} barras</p>
            </div>
            <button
              type="button"
              onClick={() => appendTopBarGroup({ quantity: 1, diameter: diameterOptions[0] })}
              className="text-xs uppercase tracking-[0.3em] text-primary"
            >
              + Añadir barras
            </button>
          </div>
          {topBarFields.length === 0 ? (
            <p className="text-xs text-slate-500">Configura al menos un grupo para definir el acero superior.</p>
          ) : (
            <div className="space-y-3">
              {topBarFields.map((field, index) => (
                <div key={field.id} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-3 items-end">
                  <div>
                    <label className="label">Cantidad</label>
                    <input
                      type="number"
                      min={1}
                      className="input input-compact"
                      {...register(`top_bars_config.${index}.quantity`, { valueAsNumber: true })}
                    />
                    {errors.top_bars_config?.[index]?.quantity && (
                      <p className="text-rose-400 text-xs mt-1">{errors.top_bars_config[index].quantity.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="label">Diámetro</label>
                    <select className="input input-compact" {...register(`top_bars_config.${index}.diameter`)}>
                      {diameterOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    {errors.top_bars_config?.[index]?.diameter && (
                      <p className="text-rose-400 text-xs mt-1">{errors.top_bars_config[index].diameter.message}</p>
                    )}
                  </div>
                  {topBarFields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeTopBarGroup(index)}
                      className="text-xs uppercase tracking-[0.3em] text-slate-500 hover:text-slate-200"
                    >
                      Quitar
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          {topBarsGroupError && <p className="text-rose-400 text-xs">{topBarsGroupError}</p>}
        </div>

        <div className="rounded-2xl border border-slate-800 bg-[#050b16]/40 p-4 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="label">Barras inferiores</p>
              <p className="text-xs text-slate-400">Total: {totalBottomBars} barras</p>
            </div>
            <button
              type="button"
              onClick={() => appendBottomBarGroup({ quantity: 1, diameter: diameterOptions[0] })}
              className="text-xs uppercase tracking-[0.3em] text-primary"
            >
              + Añadir barras
            </button>
          </div>
          {bottomBarFields.length === 0 ? (
            <p className="text-xs text-slate-500">Detalla cada grupo de barras inferiores según el refuerzo.</p>
          ) : (
            <div className="space-y-3">
              {bottomBarFields.map((field, index) => (
                <div key={field.id} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-3 items-end">
                  <div>
                    <label className="label">Cantidad</label>
                    <input
                      type="number"
                      min={1}
                      className="input input-compact"
                      {...register(`bottom_bars_config.${index}.quantity`, { valueAsNumber: true })}
                    />
                    {errors.bottom_bars_config?.[index]?.quantity && (
                      <p className="text-rose-400 text-xs mt-1">{errors.bottom_bars_config[index].quantity.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="label">Diámetro</label>
                    <select className="input input-compact" {...register(`bottom_bars_config.${index}.diameter`)}>
                      {diameterOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    {errors.bottom_bars_config?.[index]?.diameter && (
                      <p className="text-rose-400 text-xs mt-1">{errors.bottom_bars_config[index].diameter.message}</p>
                    )}
                  </div>
                  {bottomBarFields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeBottomBarGroup(index)}
                      className="text-xs uppercase tracking-[0.3em] text-slate-500 hover:text-slate-200"
                    >
                      Quitar
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          {bottomBarsGroupError && <p className="text-rose-400 text-xs">{bottomBarsGroupError}</p>}
        </div>
      </div>
    </div>

    <div className="rounded-3xl border border-slate-800 bg-[#030916]/40 p-5 space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-[0.4em] text-slate-500">Refuerzo para tramos específicos</p>
        <button
          type="button"
          disabled={spanOptions.length === 0}
          onClick={() =>
            appendSegmentReinforcement({
              span_indexes: [],
              top_quantity: '',
              top_diameter: '',
              bottom_quantity: '',
              bottom_diameter: '',
            })
          }
          className={`text-xs uppercase tracking-[0.3em] transition-colors ${
            spanOptions.length === 0 ? 'text-slate-600 cursor-not-allowed' : 'text-primary'
          }`}
        >
          + Agregar
        </button>
      </div>
      {segmentReinforcementFields.length === 0 ? (
        <p className="text-xs text-slate-500">
          Define aquí refuerzos puntuales cuando requieras barras adicionales en luces o zonas críticas.
        </p>
      ) : (
        <div className="space-y-4">
          {segmentReinforcementFields.map((field, index) => (
            <div key={field.id} className="rounded-2xl border border-slate-800 bg-[#050b16]/40 p-4 space-y-4">
              <div className="flex items-center justify-between">
                <p className="label mb-0">Tramos objetivo</p>
                <button
                  type="button"
                  onClick={() => removeSegmentReinforcement(index)}
                  className="text-xs uppercase tracking-[0.3em] text-slate-500 hover:text-slate-200"
                >
                  Quitar
                </button>
              </div>
              <Controller
                control={control}
                name={`segment_reinforcements.${index}.span_indexes`}
                defaultValue={field.span_indexes || []}
                render={({ field: spanField }) => {
                  const selectedValues = Array.isArray(spanField.value) ? spanField.value : [];
                  const toggleSpan = (spanIndex) => {
                    const exists = selectedValues.includes(spanIndex);
                    const updated = exists
                      ? selectedValues.filter((value) => value !== spanIndex)
                      : [...selectedValues, spanIndex];
                    spanField.onChange(updated);
                  };
                  return (
                    <div className="flex flex-wrap gap-2">
                      {spanOptions.length ? (
                        spanOptions.map((option) => {
                          const isActive = selectedValues.includes(option.value);
                          return (
                            <button
                              type="button"
                              key={option.value}
                              onClick={() => toggleSpan(option.value)}
                              className={`px-3 py-1 rounded-full border text-[11px] font-semibold tracking-[0.25em] transition-colors ${
                                isActive ? 'bg-primary/80 text-white border-primary' : 'border-slate-700 text-slate-300'
                              }`}
                            >
                              <span>{option.label}</span>
                              {option.lengthLabel && <span className="block text-[10px] text-slate-400">{option.lengthLabel}</span>}
                            </button>
                          );
                        })
                      ) : (
                        <p className="text-xs text-slate-500">Crea al menos una luz para seleccionar tramos.</p>
                      )}
                    </div>
                  );
                }}
              />
              {errors.segment_reinforcements?.[index]?.span_indexes && (
                <p className="text-rose-400 text-xs">
                  {errors.segment_reinforcements[index].span_indexes.message}
                </p>
              )}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-slate-800 bg-[#040b18]/60 p-4 space-y-2">
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Superior</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="label">Cantidad</label>
                      <input
                        type="number"
                        min={1}
                        className="input input-compact"
                        {...register(`segment_reinforcements.${index}.top_quantity`, { valueAsNumber: true })}
                      />
                      {errors.segment_reinforcements?.[index]?.top_quantity && (
                        <p className="text-rose-400 text-xs mt-1">
                          {errors.segment_reinforcements[index].top_quantity.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="label">Diámetro</label>
                      <select className="input input-compact" {...register(`segment_reinforcements.${index}.top_diameter`)}>
                        <option value="">Φ</option>
                        {diameterOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-800 bg-[#040b18]/60 p-4 space-y-2">
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Inferior</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="label">Cantidad</label>
                      <input
                        type="number"
                        min={1}
                        className="input input-compact"
                        {...register(`segment_reinforcements.${index}.bottom_quantity`, { valueAsNumber: true })}
                      />
                      {errors.segment_reinforcements?.[index]?.bottom_quantity && (
                        <p className="text-rose-400 text-xs mt-1">
                          {errors.segment_reinforcements[index].bottom_quantity.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="label">Diámetro</label>
                      <select className="input input-compact" {...register(`segment_reinforcements.${index}.bottom_diameter`)}>
                        <option value="">Φ</option>
                        {diameterOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {segmentReinforcementsError && <p className="text-rose-400 text-xs">{segmentReinforcementsError}</p>}
    </div>

    <div className="bg-[#0b172f] rounded-3xl border border-slate-800 p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-[0.3em] text-slate-400">Estribos</h2>
        <button
          type="button"
          onClick={() =>
            appendStirrup({
              additional_branches: 0,
              stirrup_type: stirrupTypeOptions[0]?.value || 'C',
            })
          }
          className="text-xs uppercase tracking-[0.3em] text-primary"
        >
          + Añadir rama
        </button>
      </div>
      <div className="space-y-3">
        {stirrupFields.map((field, index) => (
          <div key={field.id} className="grid md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-3 items-start">
            <div>
              <label className="label">Ramas adicionales</label>
              <input
                type="number"
                min={0}
                className="input"
                {...register(`stirrups_config.${index}.additional_branches`, { valueAsNumber: true })}
              />
              {errors.stirrups_config?.[index]?.additional_branches && (
                <p className="text-rose-400 text-xs mt-1">
                  {errors.stirrups_config[index].additional_branches.message}
                </p>
              )}
            </div>
            <div>
              <label className="label">Tipo de estribo</label>
              <select className="input" {...register(`stirrups_config.${index}.stirrup_type`)}>
                {stirrupTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            {stirrupFields.length > 1 && (
              <button
                type="button"
                onClick={() => removeStirrup(index)}
                className="text-xs uppercase tracking-[0.3em] text-slate-400 self-end"
              >
                Quitar
              </button>
            )}
          </div>
        ))}
        {errors.stirrups_config?.message && <p className="text-rose-400 text-xs">{errors.stirrups_config.message}</p>}
      </div>
    </div>
    </div>
  );
};

export default BlockReinforcement;
